import { useMemo, useState } from "react";

/* QuizDuell — multiple choice, ARENA edition. Big glass stage in the world's
   accent colour, letter chips, pop/shake feedback, live streak flame.
   Calls onFinish({correct,total}). */
export default function QuizDuell({ module, accent = "#8b5cf6", onFinish }) {
  const questions = useMemo(() => (module.questions || []).map((q) => {
    const order = q.options.map((_, i) => i).sort(() => Math.random() - 0.5);
    return { ...q, _opts: order.map((i) => q.options[i]), _answer: order.indexOf(q.answer) };
  }), [module.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  const q = questions[idx];
  const locked = picked !== null;
  const correct = locked && picked === q._answer;

  const choose = (i) => {
    if (locked) return;
    setPicked(i);
    if (i === q._answer) { setScore((s) => s + 1); setStreak((s) => s + 1); }
    else setStreak(0);
  };
  const next = () => {
    if (idx + 1 < questions.length) { setIdx((i) => i + 1); setPicked(null); }
    else onFinish({ correct: score, total: questions.length });
  };

  return (
    <div className="mx-auto w-full max-w-3xl">
      <ProgressDots total={questions.length} idx={idx} accent={accent} />

      <div key={idx} className="mt-5 rounded-[2rem] border p-6 backdrop-blur-xl sm:p-9"
        style={{
          animation: "arena-in 0.35s ease both",
          borderColor: locked ? (correct ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)") : `${accent}33`,
          background: `linear-gradient(165deg, ${accent}0e 0%, rgba(8,6,26,0.85) 55%)`,
          boxShadow: `0 0 44px ${accent}1c, 0 20px 50px rgba(0,0,0,0.45)`,
        }}>
        <div className="mb-2 flex items-center justify-between">
          <span className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest" style={{ background: `${accent}1c`, color: accent, border: `1px solid ${accent}40` }}>
            Frage {idx + 1} / {questions.length}
          </span>
          {streak >= 2 && <span className="animate-pulse text-sm font-black text-gold">🔥 {streak}er-Streak</span>}
        </div>
        <h3 className="text-xl font-black leading-snug sm:text-3xl">{q.q}</h3>

        <div className="mt-6 grid gap-3">
          {q._opts.map((opt, i) => {
            const isAns = i === q._answer;
            const state = !locked ? "idle" : isAns ? "right" : i === picked ? "wrong" : "dim";
            return (
              <button key={i} onClick={() => choose(i)} disabled={locked}
                style={{
                  ...(state === "idle" ? { borderColor: "rgba(255,255,255,0.12)" } : {}),
                  ...(state === "wrong" ? { animation: "sx-shake 0.4s" } : {}),
                  ...(state === "right" ? { boxShadow: "0 0 26px rgba(34,197,94,0.35)" } : {}),
                }}
                className={[
                  "group flex items-center gap-3.5 rounded-2xl border px-5 py-4 text-left text-base font-bold transition-all sm:text-lg",
                  state === "idle" && "bg-white/[0.04] text-white hover:-translate-y-0.5 hover:bg-white/[0.08] active:scale-[0.99]",
                  state === "right" && "border-green/70 bg-green/20 text-white",
                  state === "wrong" && "border-red/70 bg-red/20 text-white",
                  state === "dim" && "border-white/8 bg-white/[0.02] text-white/40",
                ].filter(Boolean).join(" ")}
                onMouseEnter={(e) => { if (!locked) e.currentTarget.style.borderColor = `${accent}80`; }}
                onMouseLeave={(e) => { if (!locked) e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}>
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-sm font-black transition-colors"
                  style={state === "right" ? { background: "#22c55e", color: "#04120a" }
                    : state === "wrong" ? { background: "#ef4444", color: "#fff" }
                    : { background: `${accent}22`, color: "#fff", border: `1px solid ${accent}45` }}>
                  {state === "right" ? "✓" : state === "wrong" ? "✕" : String.fromCharCode(65 + i)}
                </span>
                <span className="flex-1">{opt}</span>
              </button>
            );
          })}
        </div>

        {locked && (
          <div className={`mt-5 rounded-2xl border p-4 text-sm ${correct ? "border-green/40 bg-green/10" : "border-cyan/40 bg-cyan/10"}`} style={{ animation: "arena-in 0.3s ease both" }}>
            <b className={correct ? "text-green" : "text-cyan"}>{correct ? "Stark! " : "Knapp! "}</b>
            <span className="text-white/80">{q.explain}</span>
          </div>
        )}
      </div>

      {locked && (
        <button onClick={next}
          className="mt-5 w-full rounded-2xl px-6 py-4 text-base font-black text-white transition-transform hover:scale-[1.01] active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${accent}, #ec4899)`, boxShadow: `0 0 30px ${accent}45, 0 12px 26px rgba(0,0,0,0.4)`, animation: "arena-in 0.3s ease both" }}>
          {idx + 1 < questions.length ? "Weiter →" : "Abschließen ✦"}
        </button>
      )}
    </div>
  );
}

export function ProgressDots({ total, idx, accent = "#8b5cf6" }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="h-2 flex-1 rounded-full transition-all duration-300"
          style={i < idx ? { background: "#22c55e", boxShadow: "0 0 8px rgba(34,197,94,0.5)" }
            : i === idx ? { background: `linear-gradient(90deg, ${accent}, #22d3ee)`, boxShadow: `0 0 10px ${accent}70` }
            : { background: "rgba(255,255,255,0.1)" }} />
      ))}
    </div>
  );
}
